import React, { createContext, useContext, useEffect, useState } from 'react';
import { LICENSE_CONFIG } from './license.config';

const CACHE_KEY = 'license_cache_v1';
const CACHE_TTL_MS = 6 * 60 * 60 * 1000;

const LicenseContext = createContext(null);
export const useLicense = () => useContext(LicenseContext);

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ cachedAt: Date.now(), data }));
  } catch {}
}

async function fetchLicenseStatus() {
  const res = await fetch(LICENSE_CONFIG.apiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${LICENSE_CONFIG.apiKey}` },
    body: JSON.stringify({ domain: LICENSE_CONFIG.domain }),
  });
  if (!res.ok) throw new Error('verify failed');
  return res.json();
}

export function LicenseGuard({ children }) {
  const [state, setState] = useState({ loading: true, data: readCache() });

  useEffect(() => {
    let cancelled = false;
    fetchLicenseStatus()
      .then((data) => {
        if (cancelled) return;
        writeCache(data);
        setState({ loading: false, data });
      })
      .catch(() => {
        if (cancelled) return;
        setState((prev) => ({ loading: false, data: prev.data }));
      });
    return () => { cancelled = true; };
  }, []);

  if (state.loading && !state.data) return <LoadingScreen />;

  const isBlocked = !state.data || state.data.status === 'blocked' || state.data.status === 'expired';
  if (isBlocked) return <PaymentRequiredScreen licenseInfo={state.data} />;

  return <LicenseContext.Provider value={state.data}>{children}</LicenseContext.Provider>;
}

function LoadingScreen() {
  return (
    <div style={s.center}>
      <div style={s.spinner} />
    </div>
  );
}

const SPARKX_PHONE = '01516585709';
const SPARKX_WEBSITE = 'https://e-commerce.sparkx-ai.shop/';

function PaymentRequiredScreen({ licenseInfo }) {
  const message = licenseInfo?.message || 'আপনার সাবস্ক্রিপশনের মেয়াদ শেষ হয়ে গেছে।';
  const waNumber = LICENSE_CONFIG.supportWhatsApp || SPARKX_PHONE;
  const waText = encodeURIComponent(`আসসালামু আলাইকুম, ${LICENSE_CONFIG.domain} এর সাবস্ক্রিপশন renew করতে চাই।`);

  return (
    <div style={s.center}>
      <div style={s.glowTop} />
      <div style={s.glowBottom} />

      <div style={s.card}>
        <div style={s.badge}>⚠ সার্ভিস বন্ধ আছে</div>

        <div style={s.logoRow}>
          <span style={s.logoSpark}>Spark</span>
          <span style={s.logoX}>X</span>
        </div>
        <div style={s.poweredBy}>Powered by SparkX IT</div>

        <h2 style={s.title}>সার্ভিস সাময়িকভাবে বন্ধ আছে</h2>
        <p style={s.message}>{message}</p>

        <div style={s.urgentBanner}>দ্রুত Renew করুন — সার্ভিস পুনরায় চালু করতে যোগাযোগ করুন</div>

        <div style={s.buttonCol}>
          <a href={`https://wa.me/${waNumber}?text=${waText}`} target="_blank" rel="noreferrer" style={s.waButton}>
            <span style={s.btnIcon}>💬</span> WhatsApp করুন
          </a>
          <a href={`tel:${SPARKX_PHONE}`} style={s.callButton}>
            <span style={s.btnIcon}>📞</span> কল করুন: {SPARKX_PHONE}
          </a>
        </div>

        <a href={SPARKX_WEBSITE} target="_blank" rel="noreferrer" style={s.websiteLink}>
          {SPARKX_WEBSITE.replace('https://', '')}
        </a>

        <div style={s.footer}>&copy; {new Date().getFullYear()} SparkX IT — All rights reserved</div>
      </div>
    </div>
  );
}

const s = {
  center: {
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(160deg, #0b1120 0%, #0f172a 50%, #0b1324 100%)',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    overflow: 'hidden',
    padding: 20,
  },
  glowTop: {
    position: 'absolute', top: -120, right: -80, width: 320, height: 320, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(56,189,248,0.25) 0%, transparent 70%)', filter: 'blur(10px)',
  },
  glowBottom: {
    position: 'absolute', bottom: -140, left: -100, width: 360, height: 360, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(34,197,94,0.18) 0%, transparent 70%)', filter: 'blur(10px)',
  },
  card: {
    position: 'relative',
    maxWidth: 440,
    width: '100%',
    padding: '36px 30px 28px',
    background: 'rgba(30,41,59,0.85)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(148,163,184,0.15)',
    borderRadius: 18,
    textAlign: 'center',
    color: '#f1f5f9',
    boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
  },
  badge: {
    display: 'inline-block', marginBottom: 18, padding: '6px 14px', fontSize: 12, fontWeight: 700,
    letterSpacing: 0.4, color: '#fca5a5', background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.35)', borderRadius: 999,
  },
  logoRow: { fontSize: 30, fontWeight: 800, letterSpacing: 0.5, lineHeight: 1 },
  logoSpark: { color: '#f8fafc' },
  logoX: { color: '#38bdf8' },
  poweredBy: { fontSize: 12, color: '#64748b', marginTop: 4, marginBottom: 20, letterSpacing: 0.3 },
  title: { margin: '0 0 10px', fontSize: 21, fontWeight: 700, color: '#f8fafc' },
  message: { margin: '0 0 18px', color: '#cbd5e1', fontSize: 14.5, lineHeight: 1.6 },
  urgentBanner: {
    margin: '0 0 22px', padding: '10px 14px', fontSize: 13.5, fontWeight: 600, color: '#fef08a',
    background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 10,
  },
  buttonCol: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 },
  waButton: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '13px 24px', background: '#22c55e', color: '#fff', borderRadius: 10,
    textDecoration: 'none', fontWeight: 700, fontSize: 15, boxShadow: '0 8px 20px rgba(34,197,94,0.25)',
  },
  callButton: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '13px 24px', background: 'transparent', color: '#38bdf8', borderRadius: 10,
    textDecoration: 'none', fontWeight: 700, fontSize: 15, border: '1.5px solid #38bdf8',
  },
  btnIcon: { fontSize: 16 },
  websiteLink: {
    display: 'inline-block', fontSize: 13, color: '#94a3b8', textDecoration: 'none',
    borderBottom: '1px dashed rgba(148,163,184,0.4)', paddingBottom: 2,
  },
  footer: { marginTop: 22, fontSize: 11, color: '#475569' },
  spinner: { width: 36, height: 36, border: '3px solid #334155', borderTopColor: '#38bdf8', borderRadius: '50%' },
};